'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { Ban, Plus, Trash2, Loader2, Search, AlertTriangle } from 'lucide-react';
import Link from 'next/link';
import { useAuth } from '@/context/AuthContext';

interface BlacklistEntry {
  id: string;
  patientId: string | null;
  phone: string;
  reason: string;
  addedBy: string;
  createdAt: string;
}

export default function BlacklistPage() {
  const { session } = useAuth();
  const [entries, setEntries] = useState<BlacklistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ phone: '', reason: '', addedBy: '' });

  const operatorName = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('lcm_session') ?? '{}')?.operator?.name ?? 'Operatore'
    : 'Operatore';

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const load = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    try {
      const res = await fetch('/api/patients/blacklist');
      const json = await res.json();
      setEntries(json.data ?? []);
    } catch (err: unknown) {
      console.error('[blacklist] load error:', err);
      setEntries([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.phone.trim()) { showMsg('error', 'Il numero di telefono è obbligatorio'); return; }
    if (!form.reason.trim()) { showMsg('error', 'Il motivo è obbligatorio'); return; }
    // Basic phone format validation
    const phoneRegex = /^\+?[\d\s\-()]{7,20}$/;
    if (!phoneRegex.test(form.phone.trim())) { showMsg('error', 'Formato numero non valido (es. +39 02 1234567)'); return; }
    try {
      const res = await fetch('/api/patients/blacklist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, addedBy: form.addedBy || operatorName }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error);
      showMsg('success', 'Numero aggiunto alla blacklist');
      setShowAdd(false);
      setForm({ phone: '', reason: '', addedBy: '' });
      load();
    } catch (err: any) { showMsg('error', err.message); }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Rimuovere questo numero dalla blacklist?')) return;
    try {
      const res = await fetch(`/api/patients/blacklist?id=${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Errore rimozione');
      showMsg('success', 'Numero rimosso dalla blacklist');
      load();
    } catch (err: any) { showMsg('error', err.message); }
  };

  const filtered = entries.filter((e) =>
    e.phone.includes(search) || e.reason.toLowerCase().includes(search.toLowerCase()) || e.addedBy.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="min-h-screen bg-background">
        <div className="px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Ban size={22} className="text-red-500" /> Blacklist / Do-Not-Call
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Numeri da non contattare mai</p>
            </div>
            <button onClick={() => setShowAdd(true)}
              className="flex items-center gap-2 px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-medium hover:bg-destructive/90">
              <Plus size={16} /> Aggiungi Numero
            </button>
          </div>

          {message && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium border ${message.type === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
              {message.text}
            </div>
          )}

          {showAdd && (
            <div className="bg-white border border-border rounded-xl p-5 mb-4">
              <h3 className="font-semibold mb-4">Aggiungi alla Blacklist</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Numero Telefono</label>
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder="+39..." />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Motivo</label>
                  <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder="Motivo blocco" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">Aggiunto da</label>
                  <input value={form.addedBy} onChange={(e) => setForm({ ...form, addedBy: e.target.value })}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm" placeholder={operatorName} />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={handleAdd} className="px-4 py-2 bg-destructive text-destructive-foreground rounded-lg text-sm font-medium">Aggiungi</button>
                <button onClick={() => setShowAdd(false)} className="px-4 py-2 border border-border rounded-lg text-sm">Annulla</button>
              </div>
            </div>
          )}

          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-border rounded-lg text-sm" placeholder="Cerca per numero, motivo..." />
          </div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="animate-spin text-muted-foreground" size={24} /></div>
          ) : (
            <div className="bg-white border border-border rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border bg-red-50">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <AlertTriangle size={14} />
                  <span className="font-medium">{filtered.length} numeri in blacklist</span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    {['Numero', 'Motivo', 'Aggiunto da', 'Data', 'Azioni'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Nessun numero in blacklist</td></tr>
                  ) : filtered.map((e) => (
                    <tr key={e.id} className="hover:bg-muted/30">
                      <td className="px-4 py-3 font-mono font-medium text-red-700">
                        {e.patientId ? (
                          <Link href={`/patients/${e.patientId}`} className="hover:underline flex items-center gap-1">
                            {e.phone} <span className="text-[10px] text-primary">→ profilo</span>
                          </Link>
                        ) : e.phone}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{e.reason}</td>
                      <td className="px-4 py-3 text-muted-foreground">{e.addedBy}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{new Date(e.createdAt).toLocaleDateString('it-IT')}</td>
                      <td className="px-4 py-3">
                        <button onClick={() => handleRemove(e.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
